import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { PlusCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { debugLog, debugWarn, debugError } from '@/lib/debugUtils';

interface NewWorkspaceDialogProps {
  onWorkspaceCreated?: (workspace: any) => void;
  trigger?: React.ReactNode;
  className?: string;
}

const NewWorkspaceDialog: React.FC<NewWorkspaceDialogProps> = ({
  onWorkspaceCreated,
  trigger,
  className = ''
}) => {
  const { userId, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    setAsDefault: false
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      setAsDefault: false
    });
  };

  // Generate suggested workspace name based on user's name
  const getSuggestedWorkspaceName = () => {
    if (!user) return '';
    
    const userName = user.firstName || 
                     user.fullName?.split(' ')[0] || 
                     user.username || 
                     'My';
    
    return `${userName}'s Workspace`;
  };

  const handleOpenChange = (newOpen: boolean) => {
    debugLog(user?.primaryEmailAddress?.emailAddress, '=== NEW WORKSPACE DIALOG OPEN CHANGE ===', newOpen);
    setIsOpen(newOpen);
    
    // When opening, suggest a default name
    if (newOpen && !formData.name) {
      const suggestedName = getSuggestedWorkspaceName();
      setFormData(prev => ({ ...prev, name: suggestedName }));
    }
  };

  const handleCreateWorkspace = async () => {
    const trimmedName = formData.name.trim();
    
    debugLog(user?.primaryEmailAddress?.emailAddress, '=== CREATING WORKSPACE ===');
    debugLog(user?.primaryEmailAddress?.emailAddress, 'Form data:', formData);
    debugLog(user?.primaryEmailAddress?.emailAddress, 'User ID:', userId);

    // Basic validation
    if (!trimmedName) {
      toast.error('Workspace name is required');
      return;
    }

    if (trimmedName.length > 100) {
      toast.error('Workspace name cannot exceed 100 characters');
      return;
    }

    if (!userId) {
      toast.error('Authentication required');
      return;
    }

    debugLog(user?.primaryEmailAddress?.emailAddress, '[NewWorkspaceDialog] Creating workspace with userId:', userId);
    setIsCreating(true);

    try {
      // If setting as default, first update existing default workspace
      if (formData.setAsDefault) {
        const { error: updateError } = await supabase
          .from('workspaces')
          .update({ is_default: false })
          .eq('user_id', userId)
          .eq('is_default', true);

        if (updateError) {
          debugWarn(user?.primaryEmailAddress?.emailAddress, 'Error updating existing default workspace:', updateError);
          // Continue anyway, this might just mean no existing default workspace
        }
      }

      // Create new workspace
      const { data: newWorkspace, error } = await supabase
        .from('workspaces')
        .insert({
          name: trimmedName,
          description: formData.description.trim() || null,
          user_id: userId,
          is_default: formData.setAsDefault,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      debugLog(user?.primaryEmailAddress?.emailAddress, '[NewWorkspaceDialog] Workspace created successfully:', newWorkspace);

      // Call parent handler
      onWorkspaceCreated?.(newWorkspace);
      
      // Close dialog and reset form
      setIsOpen(false);
      resetForm();
      
      toast.success(`Workspace "${trimmedName}" created successfully`);
      
    } catch (error: any) {
      debugError(user?.primaryEmailAddress?.emailAddress, 'Error creating workspace:', error);
      toast.error(error.message || 'Failed to create workspace');
    } finally {
      setIsCreating(false);
    }
  };

  const defaultTrigger = (
    <Button 
      className={`bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm ${className}`}
    >
      <PlusCircle className="mr-2 h-4 w-4" />
      New Workspace
    </Button>
  );

  debugLog(user?.primaryEmailAddress?.emailAddress, '=== NEW WORKSPACE DIALOG RENDER ===');
  debugLog(user?.primaryEmailAddress?.emailAddress, 'isOpen:', isOpen);
  debugLog(user?.primaryEmailAddress?.emailAddress, 'userId:', userId);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] shadow-lg">
        <DialogHeader>
          <DialogTitle>Create New Workspace</DialogTitle>
          <DialogDescription>
            Create a new workspace to organize your keyword research projects.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="workspace-name">
              Workspace Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="workspace-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter workspace name"
              maxLength={100}
              className="shadow-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleCreateWorkspace();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              {formData.name.length}/100 characters
            </p>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="workspace-description">Description (Optional)</Label>
            <Textarea
              id="workspace-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the purpose of this workspace..."
              rows={3}
              maxLength={500}
              className="shadow-sm"
            />
            <p className="text-xs text-muted-foreground">
              {formData.description.length}/500 characters
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="set-as-default"
              checked={formData.setAsDefault}
              onCheckedChange={(checked) => 
                setFormData(prev => ({ ...prev, setAsDefault: checked as boolean }))
              }
            />
            <Label 
              htmlFor="set-as-default" 
              className="text-sm font-normal cursor-pointer"
            >
              Set as default workspace
            </Label>
          </div>
          
          {formData.setAsDefault && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-md shadow-sm">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                This will become your new default workspace. Your current default workspace will remain accessible.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              setIsOpen(false);
              resetForm();
            }}
            disabled={isCreating}
            className="shadow-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            disabled={isCreating || !formData.name.trim()}
            className="shadow-sm"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Workspace'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewWorkspaceDialog;